// ── Print service — çap yönləndirici (FiscalSTOK LAN modeli) ────────────────
// 1) Desktop POS app (WebView2) → C# host → Windows printer. DƏYİŞMİR.
// 2) Mobil app (iOS/Android WebView) → HTTP fetch → PC-dəki C# LAN serveri (:6795/print)
//    → PC öz mövcud məntiqi ilə çap edir → çeklər desktop ilə BİREBİR EYNİ.
//    PC-nin IP-si DB-dən gəlir (desktop yazır, mobil oxuyur). PC açıq olmalıdır.
// 3) Web-də C#/LAN yoxdursa VƏ superadmin icazə veribsə → brauzerin öz çap
//    dialoqu (window.print) açılır; müştəri printeri seçib çap edir.

import api from '../api/axios';

const WEBVIEW_TIMEOUT_MS = 20000;
const LAN_TIMEOUT_MS     = 20000;
const LAN_PORT_DEFAULT   = 6795;

// ── Printer siyahısı (60s cache) — getReceiptPrinterName üçün ────────────────
let _printersCache = { list: null, ts: 0 };
const getPrintersCached = async () => {
    const now = Date.now();
    if (_printersCache.list && (now - _printersCache.ts) < 60000) return _printersCache.list;
    try {
        const { data } = await api.get('/printers');
        _printersCache = { list: data?.data || [], ts: now };
        return _printersCache.list;
    } catch { return _printersCache.list || []; }
};

let _receiptPrinterCache = { name: null, ts: 0 };
export const getReceiptPrinterName = async () => {
    const now = Date.now();
    if (_receiptPrinterCache.name && (now - _receiptPrinterCache.ts) < 60000) return _receiptPrinterCache.name;
    const list = await getPrintersCached();
    const chosen = list.find(p => Number(p.is_receipt_printer) === 1 && Number(p.active) === 1);
    const name = chosen?.name || 'xp-kassa';
    _receiptPrinterCache = { name, ts: now };
    return name;
};

// ── Desktop WebView2 körpüsü (POS app) — DƏYİŞMİR ────────────────────────────
const hasWebViewBridge = () =>
    typeof window !== 'undefined' && window.chrome && window.chrome.webview &&
    typeof window.chrome.webview.postMessage === 'function';

const sendViaWebView = (message, responseType) => new Promise((resolve, reject) => {
    if (!hasWebViewBridge()) { reject(new Error('Çap yalnız Fiscal POS proqramından mümkündür.')); return; }
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const timeoutId = setTimeout(() => {
        try { window.chrome.webview.removeEventListener('message', handler); } catch { /* ignore */ }
        reject(new Error('Çap cavabı gözlənilən vaxtda gəlmədi.'));
    }, WEBVIEW_TIMEOUT_MS);
    const handler = (event) => {
        const payload = event?.data;
        if (!payload || payload.type !== responseType || payload.requestId !== requestId) return;
        clearTimeout(timeoutId);
        try { window.chrome.webview.removeEventListener('message', handler); } catch { /* ignore */ }
        if (payload.ok) resolve(payload);
        else reject(new Error(payload.error || 'Çap baş tutmadı.'));
    };
    window.chrome.webview.addEventListener('message', handler);
    window.chrome.webview.postMessage({ ...message, requestId });
});

// ── Fiskal relay: serverin qurduğu sorğunu C# üzərindən lokal kassa cihazına ötür ──
// Cihaz private LAN IP-də olduğu üçün bulud çata bilmir; C# eyni şəbəkədədir.
const FISCAL_RELAY_TIMEOUT_MS = 65000; // Z hesabat və s. uzun çəkə bilər

export const canFiscalRelay = () => hasWebViewBridge();

export const fiscalForward = (relay) => new Promise((resolve, reject) => {
    if (!hasWebViewBridge()) { reject(new Error('Fiskal əməliyyat yalnız Fiscal POS proqramında mümkündür.')); return; }
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const timeoutId = setTimeout(() => {
        try { window.chrome.webview.removeEventListener('message', handler); } catch { /* ignore */ }
        reject(new Error('Kassa cihazı vaxtında cavab vermədi.'));
    }, FISCAL_RELAY_TIMEOUT_MS);
    const handler = (event) => {
        const payload = event?.data;
        if (!payload || payload.type !== 'fiscal-forward-result' || payload.requestId !== requestId) return;
        clearTimeout(timeoutId);
        try { window.chrome.webview.removeEventListener('message', handler); } catch { /* ignore */ }
        if (payload.ok) resolve({ status: payload.status, raw: payload.raw });
        else reject(new Error(payload.error || 'Kassa cihazı ilə əlaqə alınmadı.'));
    };
    window.chrome.webview.addEventListener('message', handler);
    window.chrome.webview.postMessage({
        type: 'fiscal-forward', url: relay.url, contentType: relay.contentType, body: relay.body, requestId,
    });
});

// ── FiscalSTOK LAN çap: PC-nin IP-sini tap → fetch ──────────────────────────
let _lanBase = null;         // "http://192.168.x.x:6795"
let _lanResolved = false;
let _nativeAllowed = false;  // superadmin brauzerin öz çap dialoquna icazə verib?

// Auth token varmı — token olmadan qorunan endpointə dəyməmək üçün (401 redirect loop-un qarşısı)
const hasAuthToken = () => { try { return !!localStorage.getItem('pos_token'); } catch { return false; } };

// Çek iri font (müştəri + mətbəx) — superadmin tenant üçün açır. /printers/pc-ip qaytarır (60s cache).
let _receiptCfg = { bigFont: false, kitchenBigFont: false, kitchenQtyX: false, kitchenConfirmExit: false, fastFood: false, ts: 0 };
const loadReceiptCfg = async () => {
    const now = Date.now();
    if (_receiptCfg.ts && (now - _receiptCfg.ts) < 60000) return _receiptCfg;
    if (!hasAuthToken()) return _receiptCfg;
    try {
        const { data } = await api.get('/printers/pc-ip');
        _receiptCfg = {
            bigFont: Number(data?.receipt_big_font ?? 0) === 1,
            kitchenBigFont: Number(data?.kitchen_big_font ?? 0) === 1,
            kitchenQtyX: Number(data?.kitchen_qty_x ?? 0) === 1,
            kitchenConfirmExit: Number(data?.kitchen_confirm_on_exit ?? 0) === 1,
            fastFood: Number(data?.fast_food_enabled ?? 0) === 1,
            ts: now,
        };
    } catch { /* köhnə dəyəri saxla */ }
    return _receiptCfg;
};
const getReceiptBigFont = async () => (await loadReceiptCfg()).bigFont;
const getKitchenBigFont = async () => (await loadReceiptCfg()).kitchenBigFont;
export const getKitchenQtyX = async () => (await loadReceiptCfg()).kitchenQtyX;
export const getKitchenConfirmExit = async () => (await loadReceiptCfg()).kitchenConfirmExit;
export const getFastFood = async () => (await loadReceiptCfg()).fastFood;

// Desktop app açılışda çağırılır: C#-in inject etdiyi IP-ni DB-yə yaz + lokal işlət.
export const registerPcPrintIp = async () => {
    try {
        if (typeof window === 'undefined' || !window.__fiscalPcLanIp) return;
        const ip = window.__fiscalPcLanIp;
        const port = window.__fiscalPcLanPort || LAN_PORT_DEFAULT;
        _lanBase = `http://${ip}:${port}`;
        window.__fiscalPrintServer = _lanBase;
        _lanResolved = true;
        // Yalnız login olunubsa DB-yə yaz (token yoxdursa 401 → redirect loop olardı)
        if (hasAuthToken()) api.put('/printers/pc-ip', { ip }).catch(() => { /* ignore */ });
    } catch { /* ignore */ }
};

// Çap ünvanını həll et: injected (desktop) → yoxsa DB-dən (mobil)
const getLanBase = async () => {
    if (typeof window !== 'undefined' && window.__fiscalPrintServer) return window.__fiscalPrintServer.replace(/\/+$/, '');
    if (typeof window !== 'undefined' && window.__fiscalPcLanIp) {
        const port = window.__fiscalPcLanPort || LAN_PORT_DEFAULT;
        _lanBase = `http://${window.__fiscalPcLanIp}:${port}`;
        window.__fiscalPrintServer = _lanBase;
        _lanResolved = true;
        return _lanBase;
    }
    if (_lanResolved) return _lanBase;
    if (!hasAuthToken()) return null; // token yoxdursa DB-yə dəymə (401 loop olmasın)
    try {
        const { data } = await api.get('/printers/pc-ip');
        _lanBase = data?.ip ? `http://${data.ip}:${LAN_PORT_DEFAULT}` : null;
        _nativeAllowed = Number(data?.web_native_print_enabled ?? 0) === 1;
        if (_lanBase && typeof window !== 'undefined') window.__fiscalPrintServer = _lanBase;
        _lanResolved = true;
    } catch { _lanBase = null; }
    return _lanBase;
};

// Superadmin brauzerin öz çap dialoquna (window.print) icazə veribmi?
const isNativePrintAllowed = async () => {
    await getLanBase(); // config-i yüklə (cache) — _nativeAllowed təyin olur
    return _nativeAllowed;
};

// ── Brauzerin öz çap dialoqu (C#/LAN yoxdursa) — gizli iframe + window.print ──
const _esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));
const _money = (v) => Number(v || 0).toFixed(2);
const _nowStamp = () => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} | ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// C# çap teması ilə birebir HTML müştəri çeki (logo + cədvəl + "Nuş olsun!" + FISCAL)
const buildBillHtml = (p = {}) => {
    const meta = (label, value) => value == null || value === ''
        ? ''
        : `<div class="mt"><span class="ml">${_esc(label)}</span><span class="mc">:</span><span class="mv">${_esc(value)}</span></div>`;
    const rows = (p.items || []).map((it) => `
        <tr>
          <td class="l">${_esc(it.name)}</td>
          <td class="r">${_esc(it.qtyLabel != null ? it.qtyLabel : it.qty)}</td>
          <td class="r">${_money(it.unitPrice)}</td>
          <td class="r">${_money(it.total)}</td>
        </tr>${it.note ? `<tr><td colspan="4" class="note">Qeyd: ${_esc(it.note)}</td></tr>` : ''}`).join('');

    const svcAmt = Number(p.serviceAmount || 0);
    const svcPct = Number(p.servicePercent || 0);
    const dep = Number(p.appliedDeposit != null ? p.appliedDeposit : p.depositAmount || 0);
    const disc = Number(p.discountAmount || 0);
    const pctLabel = (svcPct % 1 === 0) ? String(svcPct) : svcPct.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    const sumLine = (label, val) => `<div class="sl"><span>${_esc(label)}</span><span>${_money(val)} ₼</span></div>`;

    // İri font rejimi (superadmin tick) — bütün ölçüləri miqyasla
    const s = p.bigFont ? 1.45 : 1;
    const f = (n) => Math.round(n * s);

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Çek</title><style>
      @page{margin:4mm}
      *{box-sizing:border-box}
      body{font-family:Tahoma,Arial,sans-serif;font-weight:bold;color:#000;width:72mm;margin:0 auto;font-size:${f(11)}px;line-height:1.3}
      .logo{display:block;max-width:60mm;max-height:70px;margin:2px auto 6px;object-fit:contain}
      .hr{border-top:1.4px solid #000;margin:5px 0}
      .title{text-align:center;font-size:${f(12)}px;margin:2px 0 6px}
      .mt{display:flex;font-size:${f(11)}px;line-height:1.55}
      .ml{width:${f(66)}px;flex:0 0 ${f(66)}px}
      .mc{width:8px;flex:0 0 8px}
      .mv{flex:1}
      table.items{width:100%;border-collapse:collapse;font-size:${f(11)}px;margin-top:2px}
      table.items th{font-weight:bold;padding:1px 0}
      table.items td{padding:1px 0;vertical-align:top}
      .l{text-align:left}
      .r{text-align:right;white-space:nowrap;padding-left:4px}
      .note{font-size:${f(8)}px;padding-left:8px}
      .sl{display:flex;justify-content:space-between;font-size:${f(11)}px;line-height:1.5}
      .total{display:flex;justify-content:space-between;font-size:${f(13)}px;margin:2px 0}
      .thanks{text-align:center;font-style:italic;font-size:${f(16)}px;margin:10px 0 12px}
      .brand{text-align:center;font-size:18px;letter-spacing:1px}
      .brandmini{text-align:center;font-size:7px;margin-top:1px}
      .url{text-align:center;font-size:${f(11)}px;margin-top:2px}
      .ffnum{text-align:center;font-weight:bold;font-size:${f(96)}px;line-height:1;margin:2px 0 2px}
      .fflabel{text-align:center;font-size:${f(11)}px;margin-bottom:4px}
    </style></head><body>
      ${(p.fastFood && p.dailyNumber) ? `<div class="ffnum">${_esc(String(p.dailyNumber))}</div><div class="fflabel">SİFARİŞ NÖMRƏSİ</div><div class="hr"></div>` : ''}
      ${p.receiptLogoUrl ? `<img class="logo" src="${_esc(p.receiptLogoUrl)}" alt="">` : ''}
      <div class="hr"></div>
      <div class="title">${_esc(p.title || 'Fiscal POS')}</div>
      ${meta('Telefon', p.phone)}
      ${meta('Tarix', _nowStamp())}
      ${meta('Sifariş No', p.orderId)}
      ${meta('Masa', p.tableName)}
      ${meta((p.waiterTitle || '').trim() || 'Ofisiant', p.waiterName)}
      <div class="hr"></div>
      <table class="items">
        <thead><tr><th class="l">Mahsul</th><th class="r">Say</th><th class="r">Qiymət</th><th class="r">Cəmi</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="hr"></div>
      ${sumLine('Cəm:', p.subtotal)}
      ${svcAmt ? sumLine(`${pctLabel}% Servis Haqqı:`, svcAmt) : ''}
      ${dep ? sumLine('Depozit:', dep) : ''}
      ${disc ? sumLine('Endirim:', disc) : ''}
      <div class="hr"></div>
      <div class="total"><span>Ümumi cəm:</span><span>${_money(p.grandTotal != null ? p.grandTotal : p.total)} ₼</span></div>
      <div class="hr"></div>
      <div class="thanks">Nuş olsun!</div>
      <div class="brand">FISCAL</div>
      <div class="brandmini">proqram təminatı</div>
      <div class="url">www.fiscalbusiness.net</div>
    </body></html>`;
};

// Sadə mətn çeki (mətbəx / hədiyyə) — monospace. bigFont=true → iri (oxunaqlı).
const buildTextHtml = (text, bigFont = false) => {
    const fs = bigFont ? 17 : 12;
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Çek</title><style>
    @page{margin:4mm}
    body{font-family:'Courier New',monospace;font-size:${fs}px;font-weight:${bigFont ? 'bold' : 'normal'};line-height:1.35;white-space:pre-wrap;width:72mm;margin:0 auto;color:#000}
  </style></head><body>${_esc(text)}</body></html>`;
};

// HTML sənədini gizli iframe-də göstər → şəkillər yüklənəndə window.print()
const browserPrintHtml = (html) => new Promise((resolve, reject) => {
    try {
        if (typeof document === 'undefined') { reject(new Error('Çap mümkün deyil.')); return; }
        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
        document.body.appendChild(iframe);
        const doc = iframe.contentWindow.document;
        doc.open(); doc.write(html); doc.close();

        let done = false;
        const cleanup = () => {
            if (done) return; done = true;
            setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* ignore */ } }, 500);
            resolve({ ok: true });
        };
        const win = iframe.contentWindow;
        win.onafterprint = cleanup;

        const doPrint = () => { try { win.focus(); win.print(); } catch (e) { cleanup(); } };
        // Şəkillər (logo) yüklənsin — yoxsa boş çıxa bilər
        const imgs = Array.from(doc.images || []);
        const pending = imgs.filter(im => !im.complete);
        if (pending.length) {
            let left = pending.length;
            const one = () => { if (--left <= 0) doPrint(); };
            pending.forEach(im => { im.addEventListener('load', one); im.addEventListener('error', one); });
            setTimeout(doPrint, 3000); // logo gec gəlsə də çap et
        } else {
            setTimeout(doPrint, 150);
        }
        setTimeout(cleanup, 60000); // ehtiyat təmizlik
    } catch (e) { reject(e instanceof Error ? e : new Error(String(e))); }
});

// Növə görə brauzer çapı: bill → C# teması HTML; kitchen/gift → mətn
const browserPrintFor = (kind, arg) => {
    if (kind === 'bill') return browserPrintHtml(buildBillHtml(arg || {}));
    if (kind === 'gift') {
        const gt = _money(arg?.grandTotal);
        const txt = ['HEDIYYE BILETI', '-------------------------------',
            `Sifaris: #${arg?.orderId || '-'}`, `Umumi cem: ${gt} AZN`, '',
            'www.fiscalbusiness.net'].join('\n');
        return browserPrintHtml(buildTextHtml(txt));
    }
    return browserPrintHtml(buildTextHtml(arg?.text || '', !!arg?.bigFont)); // kitchen
};

// Web-də çapı həll et.
// 1) Mobil app (cihaza inject olunmuş LAN ünvanı var) → real LAN çapı, alınmasa
//    icazə varsa brauzer dialoquna düş.
// 2) Adi brauzer (inject yoxdur): superadmin tick veribsə → BİRBAŞA brauzer dialoqu.
//    C#/LAN ünvanını axtarıb 20s gözləmə YOX (əks halda hər çapda gec fırlanırdı).
const webPrint = async (kind, message, printer, arg) => {
    // Yalnız mobil/desktop app-ın inject etdiyi ünvan real LAN çapı deməkdir.
    const injectedBase = (typeof window !== 'undefined' && window.__fiscalPrintServer)
        ? String(window.__fiscalPrintServer).replace(/\/+$/, '') : null;

    if (injectedBase) {
        try {
            return await lanPrint(message);
        } catch (lanErr) {
            // LAN alınmadı — icazə varsa brauzerin öz çapına düş, yoxsa xətanı ötür
            if (await isNativePrintAllowed()) return browserPrintFor(kind, arg);
            throw lanErr;
        }
    }

    // Adi brauzer: tick açıqdırsa dərhal brauzer dialoqu — C# axtarma
    if (await isNativePrintAllowed()) return browserPrintFor(kind, arg);

    // İcazə yoxdur — köhnə davranış: DB-dəki PC IP-ni sına
    const base = await getLanBase();
    if (base) return await lanPrint(message);
    throw new Error('PC çap ünvanı tapılmadı — desktop proqram açıq və eyni Wi-Fi-də olmalıdır.');
};

// Cache-i təzələ (məs. PC IP dəyişибsə)
export const clearAgentCache = () => { _printersCache = { list: null, ts: 0 }; _lanBase = null; _lanResolved = false; _nativeAllowed = false; };

// Mobil → PC LAN serverinə HTTP POST (iOS-da native proxy fetch-i ötürür)
const lanPrint = async (message) => {
    const base = await getLanBase();
    if (!base) throw new Error('PC çap ünvanı tapılmadı — desktop proqram açıq və eyni Wi-Fi-də olmalıdır.');
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), LAN_TIMEOUT_MS);
    try {
        const res = await fetch(`${base}/print`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(message),
            signal: ctrl.signal,
        });
        const j = await res.json().catch(() => ({}));
        if (!res.ok || !j.ok) throw new Error(j.error || 'Çap baş tutmadı.');
        return { ok: true };
    } catch (e) {
        throw (e instanceof Error ? e : new Error(String(e)));
    } finally { clearTimeout(timer); }
};

// ── Public API ───────────────────────────────────────────────────────────────
export const printBill = async (printer, payload) => {
    // Müştəri çeki iri font (superadmin tick) — payload-a bigFont əlavə et (C# və brauzer oxuyur)
    const bigFont = await getReceiptBigFont();
    const fastFood = await getFastFood(); // fast food rejimi → çekdə böyük sıra nömrəsi
    const p = { ...payload, bigFont, fastFood };
    if (hasWebViewBridge()) return sendViaWebView({ type: 'print-bill', printer, payload: p }, 'print-bill-result');
    return webPrint('bill', { type: 'print-bill', printer, payload: p }, printer, p);
};

// Uzun məhsul adı / qeyd sətirlərini çapdan ƏVVƏL sözlərə görə böl.
// Səbəb: C# (USB) hər marker sətrinə yalnız 1 sətir hündürlüyü verir — iri fontda ad ikinci sətrə
// keçəndə növbəti sətrin («Qeyd») üstünə düşürdü. LAN (ESC/POS) isə uzun adı kəsirdi.
// Bölünmüş davam sətirləri markersiz gedir (hər iki yolda məhsul ölçüsündə çap olunur).
// Limitlər 80 mm kağızda Tahoma bold üçün ehtiyatlı götürülüb.
const KITCHEN_WRAP = {
    big:    { item: 13, note: 22 },
    normal: { item: 21, note: 36 },
};
const wrapWords = (text, max) => {
    const out = [];
    let cur = '';
    for (const word of String(text || '').split(/\s+/).filter(Boolean)) {
        let w = word;
        while (w.length > max) { // çox uzun tək söz → hissələrə
            if (cur) { out.push(cur); cur = ''; }
            out.push(w.slice(0, max));
            w = w.slice(max);
        }
        if (!cur) cur = w;
        else if ((cur + ' ' + w).length <= max) cur += ' ' + w;
        else { out.push(cur); cur = w; }
    }
    if (cur) out.push(cur);
    return out.length ? out : [''];
};
export const wrapKitchenText = (text, bigFont = false) => {
    const lim = bigFont ? KITCHEN_WRAP.big : KITCHEN_WRAP.normal;
    const res = [];
    for (const line of String(text || '').split('\n')) {
        if (line.startsWith('@LR@')) {
            const body = line.slice(4);
            const sep = body.lastIndexOf('|');
            const left = sep >= 0 ? body.slice(0, sep) : body;
            const right = sep >= 0 ? body.slice(sep + 1) : '';
            const parts = wrapWords(left, lim.item);
            res.push(`@LR@${parts[0]}|${right}`);
            parts.slice(1).forEach((p) => res.push(p));       // davam: markersiz, məhsul ölçüsü
        } else if (line.startsWith('@I@')) {
            const parts = wrapWords(line.slice(3), lim.note);
            parts.forEach((p, i) => res.push(`@I@${i === 0 ? '' : '  '}${p}`));
        } else {
            res.push(line);
        }
    }
    return res.join('\n');
};

// ip → LAN mətbəx printeri; PC bu IP-yə birbaşa TCP göndərir (desktop ilə eyni)
export const printKitchen = async (printer, rawText, ip = '') => {
    // Mətbəx çeki iri font (superadmin tick) — C# və brauzer oxuyur
    const bigFont = await getKitchenBigFont();
    const text = wrapKitchenText(rawText, bigFont);
    if (hasWebViewBridge()) return sendViaWebView({ type: 'print-kitchen', printer, text, ip, bigFont }, 'print-kitchen-result');
    return webPrint('kitchen', { type: 'print-kitchen', printer, text, ip, bigFont }, printer, { text, bigFont });
};

export const printGift = async (printer, payload) => {
    if (hasWebViewBridge()) return sendViaWebView({ type: 'print-gift', printer, payload }, 'print-gift-result');
    return webPrint('gift', { type: 'print-gift', printer, payload }, printer, payload);
};

// Çap mümkündürmü — desktop app, (mobil) PC IP, və ya brauzerin öz çapı (icazə varsa)
export const isPrintingAvailable = async () => {
    if (hasWebViewBridge()) return true;
    const base = await getLanBase();
    if (base) return true;
    return await isNativePrintAllowed();
};
